import React from "react"

import { Button, Input, Label, Switch } from "@coval/ui"
import { Users } from "lucide-react"

/**
 * Form for creating or editing a saved view. Domain-agnostic — used by both
 * monitoring and runs via their respective dropdowns.
 */
interface SaveViewFormProps {
  viewName: string
  onViewNameChange: (name: string) => void
  viewDescription: string
  onViewDescriptionChange: (description: string) => void
  isShared: boolean
  onIsSharedChange: (shared: boolean) => void
  onSave: () => void
  onCancel: () => void
  isLoading: boolean
}

export const SaveViewForm: React.FC<SaveViewFormProps> = ({
  viewName,
  onViewNameChange,
  viewDescription,
  onViewDescriptionChange,
  isShared,
  onIsSharedChange,
  onSave,
  onCancel,
  isLoading,
}) => {
  const viewNameId = React.useId()
  const descriptionId = React.useId()

  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <Label htmlFor={viewNameId}>
          View Name <span className="text-coval-danger">*</span>
        </Label>
        <Input
          id={viewNameId}
          placeholder="e.g., Failed runs last week"
          value={viewName}
          onChange={(e) => onViewNameChange(e.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={descriptionId}>Description</Label>
        <Input
          id={descriptionId}
          placeholder="Optional description"
          value={viewDescription}
          onChange={(e) => onViewDescriptionChange(e.target.value)}
        />
      </div>
      <div className="flex items-start justify-between py-2 gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Users className="w-4 h-4 text-coval-text-muted mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">Share with team</div>
            <div className="text-xs text-coval-text-muted leading-relaxed">
              Make available to all members
            </div>
          </div>
        </div>
        <Switch
          checked={isShared}
          onCheckedChange={onIsSharedChange}
          aria-label="Share with team"
          className="shrink-0"
        />
      </div>
      <div className="flex gap-3 pt-2">
        <Button
          onClick={onSave}
          disabled={isLoading || !viewName.trim()}
          className="flex-1"
        >
          Save View
        </Button>
        <Button variant="secondary" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
      </div>
    </div>
  )
}
